import type { PurchaseDetails } from "@shared/purchaseEvent";

// Once-only guard for the GA4 `purchase` event. Kept in localStorage so a second tab, a reload or a later visit to the same
// success link cannot fire it again (it was per tab before). If storage is blocked it falls back to sessionStorage, then to
// memory for this page view. Only a Stripe session id (a payment reference, not a person) is stored. GA4 also drops a
// repeated transaction_id, so this is the second of two guards.
const KEY = "fdp-purchase-fired";
const memory = new Set<string>();

type Store = Pick<Storage, "getItem" | "setItem">;

function stores(): Store[] {
  const out: Store[] = [];
  try { if (typeof window !== "undefined") out.push(window.localStorage); } catch { /* blocked */ }
  try { if (typeof window !== "undefined") out.push(window.sessionStorage); } catch { /* blocked */ }
  return out;
}

function read(list: Store[]): string[] {
  const seen: string[] = [];
  for (const s of list) {
    try { seen.push(...(JSON.parse(s.getItem(KEY) ?? "[]") as string[])); } catch { /* ignore */ }
  }
  return seen;
}

export function shouldFirePurchase(transactionId: string, list: Store[] = stores()): boolean {
  return !!transactionId && !memory.has(transactionId) && !read(list).includes(transactionId);
}

export function markPurchaseFired(transactionId: string, list: Store[] = stores()): void {
  memory.add(transactionId);
  for (const s of list) {
    try { s.setItem(KEY, JSON.stringify(Array.from(new Set([...read([s]), transactionId])).slice(-50))); } catch { /* memory only */ }
  }
}

/**
 * The GA4 `purchase` parameters, or null when the event must not fire: only a countable session (live mode, paid, real
 * amount) produces one. Value and currency are Stripe's own, transaction_id is the Checkout session id.
 */
export function purchaseParams(d: PurchaseDetails | undefined | null): Record<string, unknown> | null {
  if (!d || !d.countable || d.skip || !(d.value > 0) || !d.transactionId) return null;
  return {
    transaction_id: d.transactionId,
    value: d.value,
    currency: d.currency,
    tier: d.tier,
    plan: d.plan,
    items: [{ item_id: `${d.tier}_${d.plan}`, item_name: `${d.tier} ${d.plan}`, price: d.value, quantity: 1 }],
  };
}

export function __resetPurchaseGuardForTests() { memory.clear(); }
